import React, { useState, useEffect, useCallback, useContext } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from '../components/ConfirmationModal';
import { ModalShell, ModalActions } from '../components/modals/core';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';
import {
  TicketIcon,
  CalendarDaysIcon,
  ClockIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  UserGroupIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  TrashIcon,
  XMarkIcon,
  BanknotesIcon,
  ArrowDownTrayIcon,
  BellIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

interface Termin {
  id: number;
  veranstaltung_id: number;
  bezeichnung: string | null;
  datum: string;
  start_zeit: string;
  end_zeit: string;
  max_teilnehmer: number;
  buchungen_count: number;
}

interface MeineBuchung {
  id: number;
  termin_id: number;
  gebucht_am: string;
  bezahlt_am: string | null;
  bezahlt_bei: string | null;
  termin_bezeichnung: string | null;
  termin_datum: string;
  termin_start: string;
  termin_end: string;
}

interface Veranstaltung {
  id: number;
  titel: string;
  beschreibung: string | null;
  preis: number;
  buchen_bis: string;
  bezahlen_bis: string;
  aktiv: number;
  erstellt_von_name: string;
  organisator_id: number | null;
  organisator_name: string | null;
  termine: Termin[];
  meine_buchung: MeineBuchung | null;
}

interface Teilnehmer {
  buchung_id: number;
  mg_nr: number;
  member_name: string;
  email: string;
  gebucht_am: string;
  bezahlt_am: string | null;
  bezahlt_bei: string | null;
  termin_bezeichnung: string | null;
  termin_datum: string;
  termin_start: string;
  termin_end: string;
  bezahlen_bis: string;
}

interface MeineBuchungDetail {
  id: number;
  veranstaltung_id: number;
  termin_id: number;
  gebucht_am: string;
  bezahlt_am: string | null;
  bezahlt_bei: string | null;
  titel: string;
  preis: number;
  bezahlen_bis: string;
  buchen_bis: string;
  termin_bezeichnung: string | null;
  termin_datum: string;
  termin_start: string;
  termin_end: string;
}

interface MemberOption {
  mg_nr: number;
  name: string;
}

const MyEvents: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [events, setEvents] = useState<Veranstaltung[]>([]);
  const [myBookings, setMyBookings] = useState<MeineBuchungDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTerminId, setSelectedTerminId] = useState<Record<number, number>>({});
  const [bookingLoading, setBookingLoading] = useState<number | null>(null);
  const [teilnehmerMap, setTeilnehmerMap] = useState<Record<number, Teilnehmer[]>>({});
  const [teilnehmerOpen, setTeilnehmerOpen] = useState<Record<number, boolean>>({});
  const [teilnehmerLoading, setTeilnehmerLoading] = useState<number | null>(null);
  const [members, setMembers] = useState<MemberOption[]>([]);

  // Organisator: Add Member Modal
  const [addMemberModal, setAddMemberModal] = useState<{ eventId: number; termine: Termin[] } | null>(null);
  const [addMemberForm, setAddMemberForm] = useState({ member_id: 0, termin_id: 0, gast_name: '', gast_email: '' });
  const [addMemberIsGuest, setAddMemberIsGuest] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const [memberHighlight, setMemberHighlight] = useState(-1);
  const memberSearchRef = React.useRef<HTMLInputElement>(null);
  const memberListRef = React.useRef<HTMLDivElement>(null);

  // Organisator: Mark Paid Modal
  const [markPaidModal, setMarkPaidModal] = useState<{ buchungId: number; memberName: string } | null>(null);
  const [markPaidDate, setMarkPaidDate] = useState(new Date().toISOString().split('T')[0]);

  // Organisator: Sort
  const [teilnehmerSort, setTeilnehmerSort] = useState<{ field: 'name' | 'status' | 'datum'; dir: 'asc' | 'desc' }>({ field: 'name', dir: 'asc' });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
    onCancel: () => {},
  });

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, bookingsRes] = await Promise.all([
        apiClient.get('/api/veranstaltungen'),
        apiClient.get('/api/veranstaltungen/meine'),
      ]);

      if (eventsRes.data.success) {
        setEvents(eventsRes.data.events);
      }
      if (bookingsRes.data.success) {
        setMyBookings(bookingsRes.data.bookings);
      }
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/members');
      if (res.data) {
        const memberList = (Array.isArray(res.data) ? res.data : res.data.members || [])
          .filter((m: any) => !m.geloescht)
          .map((m: any) => ({
            mg_nr: m.mg_nr,
            name: `${m.vorname || ''} ${m.name || ''}`.trim(),
          }))
          .sort((a: MemberOption, b: MemberOption) => a.name.localeCompare(b.name, 'de'));
        setMembers(memberList);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Mitglieder:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchMembers();
  }, [fetchData, fetchMembers]);

  useEffect(() => {
    if (memberListRef.current && memberHighlight >= 0) {
      const items = memberListRef.current.querySelectorAll('button');
      if (items[memberHighlight]) {
        items[memberHighlight].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [memberHighlight]);

  const toggleTeilnehmer = async (eventId: number) => {
    if (teilnehmerOpen[eventId]) {
      setTeilnehmerOpen((prev) => ({ ...prev, [eventId]: false }));
      return;
    }

    setTeilnehmerLoading(eventId);
    try {
      const res = await apiClient.get(`/api/veranstaltungen/organisator/${eventId}/teilnehmer`);
      if (res.data.success) {
        setTeilnehmerMap((prev) => ({ ...prev, [eventId]: res.data.participants }));
        setTeilnehmerOpen((prev) => ({ ...prev, [eventId]: true }));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Teilnehmer:', err);
    } finally {
      setTeilnehmerLoading(null);
    }
  };

  const handleAddMember = async () => {
    if (!addMemberModal || !addMemberForm.termin_id) return;
    if (!addMemberIsGuest && !addMemberForm.member_id) return;
    if (addMemberIsGuest && !addMemberForm.gast_name.trim()) return;

    try {
      const payload: any = { termin_id: addMemberForm.termin_id };
      if (addMemberIsGuest) {
        payload.gast_name = addMemberForm.gast_name.trim();
        payload.gast_email = addMemberForm.gast_email.trim() || undefined;
      } else {
        payload.member_id = addMemberForm.member_id;
      }

      await apiClient.post(`/api/veranstaltungen/admin/${addMemberModal.eventId}/buchen-fuer`, payload);
      const evId = addMemberModal.eventId;
      setAddMemberModal(null);
      setAddMemberForm({ member_id: 0, termin_id: 0, gast_name: '', gast_email: '' });
      setAddMemberIsGuest(false);
      setMemberSearch('');
      setMemberDropdownOpen(false);

      await fetchData();
      if (teilnehmerOpen[evId]) {
        const res = await apiClient.get(`/api/veranstaltungen/organisator/${evId}/teilnehmer`);
        if (res.data.success) setTeilnehmerMap((prev) => ({ ...prev, [evId]: res.data.participants }));
      }

      setConfirmModal({ isOpen: true, type: 'success', title: 'Hinzugefügt', message: 'Erfolgreich gebucht.', variant: 'single', onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })), onCancel: () => {} });
    } catch (err: any) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Fehler', message: err.response?.data?.message || 'Fehler beim Hinzufügen', variant: 'single', onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })), onCancel: () => {} });
    }
  };

  const handleRemoveBooking = (buchungId: number, memberName: string, eventId: number) => {
    setConfirmModal({
      isOpen: true, type: 'warning', title: 'Buchung entfernen',
      message: `Buchung von "${memberName}" wirklich entfernen?`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((p) => ({ ...p, isOpen: false }));
        try {
          await apiClient.delete(`/api/veranstaltungen/admin/buchungen/${buchungId}`);
          await fetchData();
          if (teilnehmerOpen[eventId]) {
            const res = await apiClient.get(`/api/veranstaltungen/organisator/${eventId}/teilnehmer`);
            if (res.data.success) setTeilnehmerMap((prev) => ({ ...prev, [eventId]: res.data.participants }));
          }
        } catch (err: any) {
          setConfirmModal({ isOpen: true, type: 'error', title: 'Fehler', message: err.response?.data?.message || 'Fehler beim Entfernen', variant: 'single', onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })), onCancel: () => {} });
        }
      },
      onCancel: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
    });
  };

  const handleMarkPaid = async () => {
    if (!markPaidModal) return;
    try {
      await apiClient.post(`/api/veranstaltungen/admin/buchungen/${markPaidModal.buchungId}/mark-paid`, {
        bezahlt_am: markPaidDate,
        bezahlt_bei: 'Manuell',
      });
      const evId = Object.keys(teilnehmerOpen).find((k) => teilnehmerOpen[parseInt(k)]);
      setMarkPaidModal(null);
      await fetchData();
      if (evId) {
        const res = await apiClient.get(`/api/veranstaltungen/organisator/${evId}/teilnehmer`);
        if (res.data.success) setTeilnehmerMap((prev) => ({ ...prev, [parseInt(evId)]: res.data.participants }));
      }
    } catch (err: any) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Fehler', message: err.response?.data?.message || 'Fehler', variant: 'single', onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })), onCancel: () => {} });
    }
  };

  const handleUnmarkPaid = (buchungId: number, memberName: string, eventId: number) => {
    setConfirmModal({
      isOpen: true, type: 'question', title: 'Bezahlt-Status entfernen',
      message: `Bezahlt-Status von "${memberName}" wirklich entfernen?`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((p) => ({ ...p, isOpen: false }));
        try {
          await apiClient.post(`/api/veranstaltungen/admin/buchungen/${buchungId}/unmark-paid`);
          await fetchData();
          if (teilnehmerOpen[eventId]) {
            const res = await apiClient.get(`/api/veranstaltungen/organisator/${eventId}/teilnehmer`);
            if (res.data.success) setTeilnehmerMap((prev) => ({ ...prev, [eventId]: res.data.participants }));
          }
        } catch (err: any) {
          setConfirmModal({ isOpen: true, type: 'error', title: 'Fehler', message: err.response?.data?.message || 'Fehler', variant: 'single', onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })), onCancel: () => {} });
        }
      },
      onCancel: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
    });
  };

  const handleExport = async (eventId: number) => {
    try {
      const res = await apiClient.get(`/api/veranstaltungen/admin/${eventId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Teilnehmer_Export.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export fehlgeschlagen:', err);
    }
  };

  const handleExportPDF = (eventId: number) => {
    const event = events.find((e) => e.id === eventId);
    const teilnehmer = teilnehmerMap[eventId];
    if (!event || !teilnehmer || teilnehmer.length === 0) return;

    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;

    const grouped: { [key: string]: { label: string; participants: Teilnehmer[] } } = {};
    teilnehmer.forEach((p) => {
      const key = `${p.termin_datum}_${p.termin_start}`;
      if (!grouped[key]) {
        const datum = new Date(p.termin_datum).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
        const zeit = `${p.termin_start?.slice(0, 5) || ''} – ${p.termin_end?.slice(0, 5) || ''} Uhr`;
        grouped[key] = {
          label: `${datum}  ·  ${zeit}${p.termin_bezeichnung ? `  ·  ${p.termin_bezeichnung}` : ''}`,
          participants: []
        };
      }
      grouped[key].participants.push(p);
    });

    const sortedGroups = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
    let isFirstPage = true;

    sortedGroups.forEach(([, group]) => {
      if (!isFirstPage) doc.addPage();
      isFirstPage = false;

      let y = margin;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140);
      doc.text('HSV Berlin-Adlershof e.V.', margin, y + 4);
      const orgName = event.organisator_name || event.erstellt_von_name;
      if (orgName) {
        doc.text(`Organisator: ${orgName}`, pageWidth - margin, y + 4, { align: 'right' });
      }
      y += 8;

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(34, 120, 74);
      doc.text(event.titel, margin, y + 6);
      y += 10;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60);
      doc.text(group.label, margin, y + 5);
      y += 8;

      doc.setFontSize(9);
      doc.setTextColor(120);
      const preis = Number(event.preis || 0);
      const info = preis > 0
        ? `${group.participants.length} Teilnehmer  ·  Preis: ${preis.toFixed(2)} €  ·  Bezahlt: ${group.participants.filter(p => p.bezahlt_am).length}  ·  Offen: ${group.participants.filter(p => !p.bezahlt_am).length}`
        : `${group.participants.length} Teilnehmer`;
      doc.text(info, margin, y + 4);
      y += 8;

      doc.setDrawColor(34, 120, 74);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 4;

      const headers = [['#', 'Name', 'Bezahlt', 'Anw.', 'Unterschrift']];
      const rows = group.participants
        .sort((a, b) => a.member_name.localeCompare(b.member_name))
        .map((p, i) => [
          String(i + 1),
          p.member_name,
          p.bezahlt_am ? 'Ja' : '—',
          '',
          '',
        ]);

      autoTable(doc, {
        head: headers,
        body: rows,
        startY: y,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 10,
          cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
          lineColor: [220, 220, 220],
          lineWidth: 0.3,
        },
        headStyles: {
          fillColor: [34, 120, 74],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
        },
        bodyStyles: { textColor: [40, 40, 40] },
        alternateRowStyles: { fillColor: [248, 250, 248] },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' as const, textColor: [150, 150, 150], fontSize: 8 },
          1: { cellWidth: 55, fontStyle: 'bold' as const },
          2: { cellWidth: 22, halign: 'center' as const },
          3: { cellWidth: 16, halign: 'center' as const },
          4: { cellWidth: 'auto' as const, minCellWidth: 60 },
        },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 2) {
            if (data.cell.raw === 'Ja') {
              data.cell.styles.textColor = [34, 120, 74];
            } else {
              data.cell.styles.textColor = [200, 80, 60];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
        didDrawCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 3) {
            const size = 4;
            const cx = data.cell.x + (data.cell.width - size) / 2;
            const cy = data.cell.y + (data.cell.height - size) / 2;
            doc.setDrawColor(160, 160, 160);
            doc.setLineWidth(0.4);
            doc.roundedRect(cx, cy, size, size, 0.8, 0.8, 'S');
          }
          if (data.section === 'body' && data.column.index === 4) {
            const lineY = data.cell.y + data.cell.height - 4;
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.3);
            doc.line(data.cell.x + 4, lineY, data.cell.x + data.cell.width - 4, lineY);
          }
        },
      } as any);
    });

    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(170);
      doc.text(
        `${event.titel}  ·  Erstellt am ${new Date().toLocaleDateString('de-DE')}  ·  Seite ${i}/${pageCount}`,
        pageWidth - margin, pageHeight - 8,
        { align: 'right' }
      );
    }

    doc.save(`Anwesenheit_${event.titel.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '_')}.pdf`);
  };

  const handleNotifyUnpaid = (eventId: number) => {
    setConfirmModal({
      isOpen: true, type: 'question', title: 'Zahlungserinnerung',
      message: 'Zahlungserinnerung an alle unbezahlten Teilnehmer senden?',
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((p) => ({ ...p, isOpen: false }));
        try {
          const res = await apiClient.post(`/api/veranstaltungen/admin/${eventId}/notify-unpaid`);
          setConfirmModal({ isOpen: true, type: 'success', title: 'Gesendet', message: res.data.message || 'Erinnerungen gesendet', variant: 'single', onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })), onCancel: () => {} });
        } catch (err: any) {
          setConfirmModal({ isOpen: true, type: 'error', title: 'Fehler', message: err.response?.data?.message || 'Fehler beim Senden', variant: 'single', onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })), onCancel: () => {} });
        }
      },
      onCancel: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
    });
  };

  const toggleSort = (field: 'name' | 'status' | 'datum') => {
    setTeilnehmerSort((prev) =>
      prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }
    );
  };

  const sortTeilnehmer = (list: Teilnehmer[]) => {
    const { field, dir } = teilnehmerSort;
    const sorted = [...list].sort((a, b) => {
      if (field === 'name') return a.member_name.localeCompare(b.member_name, 'de');
      if (field === 'status') {
        const statusVal = (t: Teilnehmer) => t.bezahlt_am ? 2 : (t.bezahlen_bis && isDeadlinePassed(t.bezahlen_bis) ? 0 : 1);
        return statusVal(a) - statusVal(b);
      }
      if (field === 'datum') return a.gebucht_am.localeCompare(b.gebucht_am);
      return 0;
    });
    return dir === 'desc' ? sorted.reverse() : sorted;
  };

  const groupByTermin = (list: Teilnehmer[]) => {
    const groups: Record<string, { label: string; datum: string; zeit: string; teilnehmer: Teilnehmer[] }> = {};
    for (const t of list) {
      const key = `${t.termin_datum}_${t.termin_start}`;
      if (!groups[key]) {
        groups[key] = {
          label: t.termin_bezeichnung || '',
          datum: t.termin_datum,
          zeit: `${t.termin_start}-${t.termin_end}`,
          teilnehmer: [],
        };
      }
      groups[key].teilnehmer.push(t);
    }
    return Object.values(groups).sort((a, b) => a.datum.localeCompare(b.datum) || a.zeit.localeCompare(b.zeit));
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const isDeadlinePassed = (deadline: string) => {
    const today = new Date().toISOString().split('T')[0];
    return today > deadline;
  };

  const handleBook = async (eventId: number) => {
    const terminId = selectedTerminId[eventId];
    if (!terminId) return;

    const event = events.find((e) => e.id === eventId);
    const termin = event?.termine.find((t) => t.id === terminId);

    setConfirmModal({
      isOpen: true,
      type: 'question',
      title: 'Termin buchen',
      message: `Möchtest du den Termin "${termin?.bezeichnung || formatDate(termin?.datum || '')}" für "${event?.titel}" buchen?${event?.preis ? ` Kosten: ${formatCurrency(event.preis)}` : ''}`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        setBookingLoading(eventId);
        try {
          const res = await apiClient.post(`/api/veranstaltungen/${eventId}/buchen`, { termin_id: terminId });
          if (res.data.success) {
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Gebucht!',
              message: 'Du hast den Termin erfolgreich gebucht.',
              variant: 'single',
              onConfirm: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
              onCancel: () => {},
            });
            await fetchData();
          }
        } catch (err: any) {
          const msg = err.response?.data?.message || 'Buchung fehlgeschlagen';
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: msg,
            variant: 'single',
            onConfirm: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
            onCancel: () => {},
          });
        } finally {
          setBookingLoading(null);
        }
      },
      onCancel: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
    });
  };

  const handleCancel = async (eventId: number) => {
    const event = events.find((e) => e.id === eventId);

    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Buchung stornieren',
      message: `Möchtest du deine Buchung für "${event?.titel}" wirklich stornieren?`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        setBookingLoading(eventId);
        try {
          const res = await apiClient.delete(`/api/veranstaltungen/${eventId}/buchen`);
          if (res.data.success) {
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Storniert',
              message: 'Deine Buchung wurde storniert.',
              variant: 'single',
              onConfirm: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
              onCancel: () => {},
            });
            await fetchData();
          }
        } catch (err: any) {
          const msg = err.response?.data?.message || 'Stornierung fehlgeschlagen';
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: msg,
            variant: 'single',
            onConfirm: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
            onCancel: () => {},
          });
        } finally {
          setBookingLoading(null);
        }
      },
      onCancel: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
    });
  };

  const getPaymentStatusBadge = (booking: MeineBuchung | MeineBuchungDetail) => {
    if (booking.bezahlt_am) {
      return { label: 'Bezahlt', className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' };
    }
    const bezahlenBis = 'bezahlen_bis' in booking ? (booking as MeineBuchungDetail).bezahlen_bis : '';
    if (bezahlenBis && isDeadlinePassed(bezahlenBis)) {
      return { label: 'Überfällig', className: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300' };
    }
    return { label: 'Offen', className: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300' };
  };

  // Stats für Hero
  const totalEvents = events.length;
  const myBookingsCount = events.filter(e => !!e.meine_buchung).length;
  const upcomingOpenCount = events.filter(e => !e.meine_buchung && !isDeadlinePassed(e.buchen_bis)).length;
  const myUnpaidCount = myBookings.filter(b => b.preis > 0 && !b.bezahlt_am).length;
  const myUnpaidSum = myBookings.filter(b => b.preis > 0 && !b.bezahlt_am).reduce((s, b) => s + Number(b.preis || 0), 0);

  const monthYearLabel = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center text-gray-900 dark:text-gray-200">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 pb-8 text-gray-900 dark:text-gray-100">
      <header className="app-page-header overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="flex flex-col gap-5 p-5 sm:p-7 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <TicketIcon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">Vereinsleben · {monthYearLabel}</p>
              <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-[28px]">Veranstaltungen</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Termine entdecken, Platz auswählen und direkt buchen</p>
            </div>
          </div>
          {myUnpaidCount > 0 && (
            <div className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-50 px-4 text-sm font-bold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:ring-amber-800">
              <ExclamationTriangleIcon className="h-5 w-5" />
              {myUnpaidSum.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € offen
            </div>
          )}
        </div>
      </header>

      <StatGroup columns={3}>
        <StatCard icon={TicketIcon} tone="neutral" label="Verfügbar" value={totalEvents} />
        <StatCard icon={CalendarDaysIcon} tone="primary" valueTone="primary" label="Buchbar" value={upcomingOpenCount} />
        <StatCard icon={CheckCircleIcon} tone="success" label="Gebucht" value={myBookingsCount} />
      </StatGroup>

      <div>

        {/* Verfügbare Veranstaltungen */}
        <section className="pb-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Verfügbare Veranstaltungen</h2>
            {totalEvents > 0 && <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{totalEvents} gesamt</span>}
          </div>

          {events.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:py-12">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
                <TicketIcon className="h-7 w-7" />
              </div>
              <h3 className="mt-4 text-base font-bold text-gray-900 dark:text-white">Noch keine Veranstaltungen</h3>
              <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">Sobald eine neue Vereinsveranstaltung freigeschaltet wird, kannst du sie hier ansehen und buchen.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => {
                const deadlinePassed = isDeadlinePassed(event.buchen_bis);
                const hasBooking = !!event.meine_buchung;
                const isOrganisator = user && event.organisator_id === (user.mg_nr || user.id);

                return (
                  <article
                    key={event.id}
                    className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]"
                  >
                    {/* Event Header */}
                    <div className="p-5 sm:p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <TicketIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">{event.titel}</h3>
                            {isOrganisator && (
                              <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full"
                                style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.30)', color: '#d8b4fe' }}>
                                Organisator
                              </span>
                            )}
                          </div>
                          {event.beschreibung && (
                            <p className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300">{event.beschreibung}</p>
                          )}
                        </div>
                        {event.preis > 0 && (
                          <div className="flex items-center gap-1 px-3 py-1.5 rounded-xl flex-shrink-0 bg-primary-500/10 border border-primary-500/25">
                            <CurrencyEuroIcon className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                            <span className="text-sm font-bold text-primary-700 dark:text-primary-300 tabular-nums">{formatCurrency(event.preis)}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                        {event.organisator_name && (
                          <div className="flex items-center gap-1">
                            <UserGroupIcon className="h-3.5 w-3.5" />
                            <span>Organisator: <span className="font-medium text-primary-700 dark:text-primary-300">{event.organisator_name}</span></span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <AttributeLabel iconKey="booking" size="sm">Buchen bis:</AttributeLabel>
                          <span className={deadlinePassed ? 'font-medium text-rose-700 dark:text-rose-300' : 'font-medium text-gray-800 dark:text-gray-200'}>{formatDate(event.buchen_bis)}</span>
                        </div>
                        {event.preis > 0 && (
                          <div className="flex items-center gap-1">
                            <AttributeLabel iconKey="payment" size="sm">Bezahlen bis:</AttributeLabel>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(event.bezahlen_bis)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bereits gebucht Info */}
                    {hasBooking && (
                      <div className="mx-4 mb-4 rounded-xl p-4 sm:mx-5"
                        style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-start gap-2 min-w-0">
                            <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                              Gebucht: {event.meine_buchung?.termin_bezeichnung || formatDate(event.meine_buchung?.termin_datum || '')}
                              {' '}{event.meine_buchung?.termin_start}-{event.meine_buchung?.termin_end}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {event.preis > 0 && (() => {
                              const badge = getPaymentStatusBadge(event.meine_buchung!);
                              return (
                                <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${badge.className}`}>
                                  {badge.label}
                                </span>
                              );
                            })()}
                            {!deadlinePassed && (
                              <button
                                onClick={() => handleCancel(event.id)}
                                disabled={bookingLoading === event.id}
                                className="text-xs text-rose-300 hover:text-rose-200 font-semibold"
                              >
                                Stornieren
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Termine */}
                    {!hasBooking && !deadlinePassed && (
                      <div className="border-t border-gray-200 dark:border-gray-700">
                        <div className="p-5 sm:p-6">
                          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Termin auswählen</h4>
                          <div className="space-y-2">
                            {event.termine.map((termin) => {
                              const isFull = termin.buchungen_count >= termin.max_teilnehmer;
                              const spotsLeft = termin.max_teilnehmer - termin.buchungen_count;
                              const selected = selectedTerminId[event.id] === termin.id;

                              return (
                                <label
                                  key={termin.id}
                                  className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition ${
                                    selected
                                      ? 'border border-primary-400/50 bg-primary-50 dark:bg-primary-500/15'
                                      : 'border border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:bg-gray-800'
                                  } ${isFull ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <input
                                    type="radio"
                                    name={`termin-${event.id}`}
                                    value={termin.id}
                                    checked={selected}
                                    onChange={() => setSelectedTerminId((prev) => ({ ...prev, [event.id]: termin.id }))}
                                    disabled={isFull}
                                    className="border-gray-300 bg-white text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(termin.datum)}</span>
                                      <span className="text-sm text-gray-600 dark:text-gray-300">
                                        <ClockIcon className="h-3.5 w-3.5 inline mr-0.5" />
                                        {termin.start_zeit?.substring(0, 5)}-{termin.end_zeit?.substring(0, 5)}
                                      </span>
                                      {termin.bezeichnung && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">({termin.bezeichnung})</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <UserGroupIcon className="h-4 w-4 text-gray-400" />
                                    <span className={`text-xs font-medium ${isFull ? 'text-rose-600 dark:text-rose-300' : spotsLeft <= 3 ? 'text-amber-600 dark:text-amber-300' : 'text-gray-600 dark:text-gray-300'}`}>
                                      {isFull ? 'Ausgebucht' : `${spotsLeft} frei`}
                                    </span>
                                  </div>
                                </label>
                              );
                            })}
                          </div>

                          <button
                            onClick={() => handleBook(event.id)}
                            disabled={!selectedTerminId[event.id] || bookingLoading === event.id}
                            className="mt-4 w-full rounded-xl bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                          >
                            {bookingLoading === event.id ? (
                              <span className="flex items-center justify-center gap-2">
                                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                Wird gebucht...
                              </span>
                            ) : (
                              'Termin buchen'
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Frist abgelaufen, nicht gebucht */}
                    {!hasBooking && deadlinePassed && (
                      <div className="mx-4 mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60 sm:mx-5">
                        <div className="flex items-center gap-2">
                          <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">Buchungsfrist abgelaufen</span>
                        </div>
                      </div>
                    )}

                    {/* Organisator: Teilnehmer verwalten */}
                    {isOrganisator && (
                      <div className="border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => toggleTeilnehmer(event.id)}
                          disabled={teilnehmerLoading === event.id}
                          className="flex w-full items-center justify-between px-5 py-3 text-sm font-semibold text-primary-700 transition hover:bg-gray-50 dark:text-primary-300 dark:hover:bg-white/5 sm:px-6"
                        >
                          <span className="flex items-center gap-2">
                            <EyeIcon className="h-4 w-4" />
                            Teilnehmer verwalten
                          </span>
                          {teilnehmerLoading === event.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-400"></div>
                          ) : (
                            <svg className={`h-4 w-4 transition-transform ${teilnehmerOpen[event.id] ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </button>

                        {teilnehmerOpen[event.id] && teilnehmerMap[event.id] && (
                          <div className="px-5 sm:px-6 pb-5">
                            <div className="flex flex-wrap gap-2 mb-3">
                              <button
                                onClick={() => setAddMemberModal({ eventId: event.id, termine: event.termine })}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-primary-700"
                              >
                                <UserPlusIcon className="h-3.5 w-3.5" />
                                Hinzufügen
                              </button>
                              <button
                                onClick={() => handleExport(event.id)}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                              >
                                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                CSV
                              </button>
                              <button
                                onClick={() => handleExportPDF(event.id)}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                              >
                                <DocumentTextIcon className="h-3.5 w-3.5" />
                                PDF
                              </button>
                              {event.preis > 0 && teilnehmerMap[event.id].some((t) => !t.bezahlt_am) && (
                                <button
                                  onClick={() => handleNotifyUnpaid(event.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl transition"
                                  style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }}
                                >
                                  <BellIcon className="h-3.5 w-3.5" />
                                  Erinnerung senden
                                </button>
                              )}
                            </div>

                            {teilnehmerMap[event.id].length === 0 ? (
                              <p className="py-3 text-sm text-gray-500 dark:text-gray-400">Noch keine Buchungen.</p>
                            ) : (() => {
                              const groups = groupByTermin(teilnehmerMap[event.id]);
                              const hasMultipleTermine = groups.length > 1;
                              const SortIcon = ({ field }: { field: 'name' | 'status' | 'datum' }) => (
                                teilnehmerSort.field === field
                                  ? (teilnehmerSort.dir === 'asc' ? <ChevronUpIcon className="h-3 w-3 inline ml-0.5" /> : <ChevronDownIcon className="h-3 w-3 inline ml-0.5" />)
                                  : <span className="inline-block w-3 h-3 ml-0.5" />
                              );

                              return (
                                <div className="space-y-4">
                                  {groups.map((group, gi) => (
                                    <div key={gi}>
                                      {hasMultipleTermine && (
                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                          <CalendarDaysIcon className="h-4 w-4 text-primary-400" />
                                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {formatDate(group.datum)} {group.zeit}
                                          </span>
                                          {group.label && <span className="text-xs text-gray-500 dark:text-gray-400">({group.label})</span>}
                                          <span className="text-xs text-gray-400 dark:text-gray-500">({group.teilnehmer.length})</span>
                                        </div>
                                      )}
                                      <div className="overflow-x-auto -mx-5 sm:mx-0">
                                        <table className="w-full text-sm table-fixed">
                                          <thead>
                                            <tr className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                                              <th className="w-[30%] cursor-pointer select-none pb-2 pl-5 font-semibold hover:text-gray-900 dark:hover:text-white sm:pl-0" onClick={() => toggleSort('name')}>
                                                Name <SortIcon field="name" />
                                              </th>
                                              <th className="pb-2 font-semibold hidden sm:table-cell w-[25%]">E-Mail</th>
                                              {event.preis > 0 && (
                                                <th className="w-[12%] cursor-pointer select-none pb-2 text-right font-semibold hover:text-gray-900 dark:hover:text-white" onClick={() => toggleSort('status')}>
                                                  Status <SortIcon field="status" />
                                                </th>
                                              )}
                                              <th className="hidden w-[18%] cursor-pointer select-none pb-2 text-right font-semibold hover:text-gray-900 dark:hover:text-white md:table-cell" onClick={() => toggleSort('datum')}>
                                                Gebucht <SortIcon field="datum" />
                                              </th>
                                              <th className="pb-2 pr-5 sm:pr-0 font-semibold text-right w-[15%]">Aktionen</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {sortTeilnehmer(group.teilnehmer).map((t) => {
                                              const isPaid = !!t.bezahlt_am;
                                              const isOverdue = !isPaid && t.bezahlen_bis && isDeadlinePassed(t.bezahlen_bis);

                                              return (
                                                <tr key={t.buchung_id} className="text-gray-800 dark:text-gray-200">
                                                  <td className="py-2 pl-5 sm:pl-0 truncate">{t.member_name}</td>
                                                  <td className="py-2 hidden sm:table-cell truncate">
                                                    <a href={`mailto:${t.email}`} className="text-primary-300 hover:text-primary-200 hover:underline text-xs">{t.email}</a>
                                                  </td>
                                                  {event.preis > 0 && (
                                                    <td className="py-2 text-right">
                                                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                                        style={isPaid
                                                          ? { background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.30)', color: '#86efac' }
                                                          : isOverdue
                                                          ? { background: 'rgba(244,63,94,0.12)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }
                                                          : { background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }}>
                                                        {isPaid ? 'Bezahlt' : isOverdue ? 'Überfällig' : 'Offen'}
                                                      </span>
                                                    </td>
                                                  )}
                                                  <td className="hidden py-2 text-right text-xs text-gray-500 dark:text-gray-400 md:table-cell">
                                                    {formatDateTime(t.gebucht_am)}
                                                  </td>
                                                  <td className="py-2 pr-5 sm:pr-0">
                                                    <div className="flex items-center justify-end gap-1">
                                                      {event.preis > 0 && !isPaid && (
                                                        <button
                                                          onClick={() => { setMarkPaidModal({ buchungId: t.buchung_id, memberName: t.member_name }); setMarkPaidDate(new Date().toISOString().split('T')[0]); }}
                                                          title="Bezahlt markieren"
                                                          className="p-1 text-primary-300 hover:bg-primary-500/15 rounded"
                                                        >
                                                          <BanknotesIcon className="h-4 w-4" />
                                                        </button>
                                                      )}
                                                      {event.preis > 0 && isPaid && (
                                                        <button
                                                          onClick={() => handleUnmarkPaid(t.buchung_id, t.member_name, event.id)}
                                                          title="Bezahlt entfernen"
                                                          className="p-1 text-amber-300 hover:bg-amber-500/15 rounded"
                                                        >
                                                          <XMarkIcon className="h-4 w-4" />
                                                        </button>
                                                      )}
                                                      <button
                                                        onClick={() => handleRemoveBooking(t.buchung_id, t.member_name, event.id)}
                                                        title="Buchung entfernen"
                                                        className="p-1 text-rose-300 hover:bg-rose-500/15 rounded"
                                                      >
                                                        <TrashIcon className="h-4 w-4" />
                                                      </button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                        {hasMultipleTermine && (
                                          <div className="mt-1 pl-5 text-xs text-gray-500 dark:text-gray-400 sm:pl-0">
                                            {group.teilnehmer.length} Teilnehmer
                                            {event.preis > 0 && <> | Bezahlt: {group.teilnehmer.filter(t => t.bezahlt_am).length}</>}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  <div className="border-t border-gray-200 pt-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                                    Gesamt: {teilnehmerMap[event.id].length} Teilnehmer
                                    {event.preis > 0 && (
                                      <> | Bezahlt: {teilnehmerMap[event.id].filter(t => t.bezahlt_am).length}</>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* Meine Buchungen */}
        {myBookings.length > 0 && (
          <section className="pb-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Meine Buchungen</h2>
            <div className="space-y-3">
              {myBookings.map((booking) => {
                const badge = getPaymentStatusBadge(booking);

                return (
                  <article
                    key={booking.id}
                    className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1c2432]"
                  >
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white">{booking.titel}</h4>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <CalendarDaysIcon className="h-3.5 w-3.5" />
                            {formatDate(booking.termin_datum)} {booking.termin_start}-{booking.termin_end}
                          </span>
                          {booking.termin_bezeichnung && <span>({booking.termin_bezeichnung})</span>}
                          <span className="flex items-center gap-1">
                            <ClockIcon className="h-3.5 w-3.5" />
                            Gebucht: {formatDateTime(booking.gebucht_am)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {booking.preis > 0 && (
                          <>
                            <span className="text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-200">{formatCurrency(booking.preis)}</span>
                            <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${badge.className}`}>{badge.label}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Add Member/Guest Modal */}
      {addMemberModal && (
        <ModalShell
          open
          onClose={() => { setAddMemberModal(null); setAddMemberIsGuest(false); setMemberSearch(''); setMemberDropdownOpen(false); }}
          variant="form"
          size="sm"
          title="Teilnehmer hinzufügen"
          icon={UserPlusIcon}
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => { setAddMemberModal(null); setAddMemberIsGuest(false); setMemberSearch(''); setMemberDropdownOpen(false); } }}
              primary={{
                label: 'Hinzufügen',
                onClick: handleAddMember,
                disabled: !addMemberForm.termin_id || (!addMemberIsGuest && !addMemberForm.member_id) || (addMemberIsGuest && !addMemberForm.gast_name.trim()),
              }}
            />
          }
        >
          <div className="space-y-4">
            <div className="flex overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => { setAddMemberIsGuest(false); setAddMemberForm((p) => ({ ...p, gast_name: '', gast_email: '' })); }}
                className={`flex-1 px-3 py-2 text-sm font-semibold transition ${!addMemberIsGuest ? 'bg-primary-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                Mitglied
              </button>
              <button
                type="button"
                onClick={() => { setAddMemberIsGuest(true); setAddMemberForm((p) => ({ ...p, member_id: 0 })); setMemberSearch(''); setMemberDropdownOpen(false); }}
                className={`flex-1 px-3 py-2 text-sm font-semibold transition ${addMemberIsGuest ? 'bg-primary-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                Gast
              </button>
            </div>

            {!addMemberIsGuest ? (
              <div className="relative">
                <AttributeLabel iconKey="member" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Mitglied</AttributeLabel>
                <input
                  ref={memberSearchRef}
                  type="text"
                  placeholder="Name eingeben zum Suchen..."
                  value={memberSearch}
                  onChange={(e) => {
                    setMemberSearch(e.target.value);
                    setMemberDropdownOpen(true);
                    setMemberHighlight(0);
                    if (!e.target.value) setAddMemberForm((p) => ({ ...p, member_id: 0 }));
                  }}
                  onFocus={() => setMemberDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setMemberDropdownOpen(false), 150)}
                  onKeyDown={(e) => {
                    const filtered = members.filter((m) => m.name.toLowerCase().includes(memberSearch.toLowerCase()));
                    if (e.key === 'ArrowDown') { e.preventDefault(); setMemberHighlight((prev) => Math.min(prev + 1, filtered.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setMemberHighlight((prev) => Math.max(prev - 1, 0)); }
                    else if (e.key === 'Enter' && memberHighlight >= 0 && filtered[memberHighlight]) {
                      e.preventDefault();
                      setAddMemberForm((p) => ({ ...p, member_id: filtered[memberHighlight].mg_nr }));
                      setMemberSearch(filtered[memberHighlight].name);
                      setMemberDropdownOpen(false);
                    } else if (e.key === 'Escape') { setMemberDropdownOpen(false); }
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
                {memberDropdownOpen && memberSearch.length > 0 && (
                  <div ref={memberListRef} className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    {members
                      .filter((m) => m.name.toLowerCase().includes(memberSearch.toLowerCase()))
                      .map((m, idx) => (
                        <button
                          key={m.mg_nr}
                          type="button"
                          onClick={() => { setAddMemberForm((p) => ({ ...p, member_id: m.mg_nr })); setMemberSearch(m.name); setMemberDropdownOpen(false); }}
                          className={`w-full px-3 py-2 text-left text-sm transition ${idx === memberHighlight ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-700'} ${addMemberForm.member_id === m.mg_nr ? 'font-semibold' : ''}`}
                        >
                          {m.name}
                        </button>
                      ))}
                    {members.filter((m) => m.name.toLowerCase().includes(memberSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Kein Mitglied gefunden</div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Name *</AttributeLabel>
                  <input type="text" placeholder="Vor- und Nachname" value={addMemberForm.gast_name}
                    onChange={(e) => setAddMemberForm((p) => ({ ...p, gast_name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                </div>
                <div>
                  <AttributeLabel iconKey="email" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">E-Mail (optional)</AttributeLabel>
                  <input type="email" placeholder="gast@example.de" value={addMemberForm.gast_email}
                    onChange={(e) => setAddMemberForm((p) => ({ ...p, gast_email: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                </div>
              </>
            )}

            <div>
              <AttributeLabel iconKey="date" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Termin</AttributeLabel>
              <select
                value={addMemberForm.termin_id || ''}
                onChange={(e) => setAddMemberForm((p) => ({ ...p, termin_id: parseInt(e.target.value) || 0 }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                <option value="">Termin auswählen...</option>
                {addMemberModal.termine.map((t) => (
                  <option key={t.id} value={t.id}>
                    {new Date(t.datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}{' '}
                    {t.start_zeit?.substring(0, 5)}-{t.end_zeit?.substring(0, 5)}
                    {t.bezeichnung ? ` (${t.bezeichnung})` : ''}
                    {' '}[{t.buchungen_count}/{t.max_teilnehmer}]
                  </option>
                ))}
              </select>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Mark Paid Modal */}
      {markPaidModal && (
        <ModalShell
          open
          onClose={() => setMarkPaidModal(null)}
          variant="form"
          size="sm"
          title="Bezahlt markieren"
          icon={BanknotesIcon}
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => setMarkPaidModal(null) }}
              primary={{ label: 'Bezahlt markieren', onClick: handleMarkPaid }}
            />
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">{markPaidModal.memberName}</p>
            <div>
              <AttributeLabel iconKey="payment" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Bezahlt am</AttributeLabel>
              <input type="date" value={markPaidDate} onChange={(e) => setMarkPaidDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
            </div>
          </div>
        </ModalShell>
      )}

      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        destructive={confirmModal.variant === 'two' && confirmModal.type === 'warning' && /stornier/i.test(confirmModal.title || '')}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        buttons={confirmModal.variant === 'two' ? {
          cancel: { text: 'Abbrechen', onPress: confirmModal.onCancel },
          confirm: { text: 'Bestätigen', onPress: confirmModal.onConfirm },
        } : undefined}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: confirmModal.onConfirm,
        } : undefined}
      />
    </div>
  );
};

export default MyEvents;
